import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, api } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { Alert, Badge, Button, Card, Field, PageHeader, Skeleton, Table } from "@/components/ui";
import { RateTester } from "@/components/RateTester";

interface EditableSetting {
  key: string;
  label: string;
  type: "secret" | "number" | "text";
  help: string;
}

interface StoredSetting {
  key: string;
  value?: string;
  is_secret: boolean;
  is_set: boolean;
  masked?: string;
  updated_by?: string;
  updated_at?: string;
}

interface SettingChange {
  id: string;
  key: string;
  action: "set" | "cleared";
  masked_hint?: string;
  actor_email: string;
  created_at: string;
}

interface SettingsPayload {
  settings: StoredSetting[];
  changes: SettingChange[];
  effective: {
    token_set: boolean;
    token_hint: string;
    account_id: number;
    provider_id: number;
    base_url: string;
    source: "settings" | "environment";
  };
  editable: EditableSetting[];
}

interface TestResult {
  ok: boolean;
  detail: string;
  services?: string[];
  rate_count?: number;
  elapsed_ms?: number;
}

/**
 * Carrier credentials, rotatable without a deploy.
 *
 * Two rules the screen is built around. The API key is never displayed, only
 * masked: a screen that can show a key is a screen that can leak one. And
 * nothing is trusted until it has been tested, because a rotation that silently
 * broke quoting would otherwise be discovered by a customer.
 */
export function PlatformSettings() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState("");
  const [result, setResult] = useState<TestResult | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["platform-settings"],
    queryFn: () => api.get<SettingsPayload>("/v1/admin/settings"),
  });

  // Non-secret fields are prefilled with what is stored, so an operator editing
  // the account id does not have to retype the provider id next to it.
  useEffect(() => {
    if (!data) return;
    const initial: Record<string, string> = {};
    for (const setting of data.settings) {
      if (!setting.is_secret && setting.value) initial[setting.key] = setting.value;
    }
    setDraft((current) => ({ ...initial, ...current }));
  }, [data]);

  const save = useMutation({
    mutationFn: () => api.put<SettingsPayload>("/v1/admin/settings", { settings: draft }),
    onMutate: () => {
      setError("");
      setFields({});
      setResult(null);
    },
    onSuccess: () => {
      // The key field is cleared after saving: leaving a secret sitting in a
      // form field is how it ends up in a screenshot.
      setDraft((current) => {
        const next = { ...current };
        delete next["shiplogic.token"];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ["platform-settings"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setFields(err.fields);
        if (Object.keys(err.fields).length === 0) setError(err.message);
      } else {
        setError("Could not save those settings.");
      }
    },
  });

  const test = useMutation({
    mutationFn: () => api.post<TestResult>("/v1/admin/settings/test"),
    onSuccess: (outcome) => setResult(outcome),
    onError: () => setResult({ ok: false, detail: "The test could not be run." }),
  });

  if (isLoading || !data) {
    return (
      <Card>
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }

  const effective = data.effective;
  const stored = (key: string) => data.settings.find((s) => s.key === key);

  return (
    <>
      <PageHeader
        title="Carrier settings"
        subtitle="ShipLogic credentials. Changes take effect on the next quote, and every change is logged."
      />

      <Card className="mb-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-bold tracking-tight">Currently in use</h2>
            <dl className="mt-3 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
              <Row label="API key" value={effective.token_set ? effective.token_hint : "Not set"} />
              <Row label="Account ID" value={String(effective.account_id || "Not set")} />
              <Row label="Provider ID" value={String(effective.provider_id || "Not set")} />
              <Row label="Base URL" value={effective.base_url} />
            </dl>
            <p className="mt-3 text-xs text-ink-500">
              {effective.source === "settings"
                ? "Read from these settings."
                : "Read from this server's environment. Anything saved below overrides it."}
            </p>
          </div>
          <div className="text-right">
            <Badge tone={effective.token_set ? "good" : "bad"}>
              {effective.token_set ? "configured" : "no key"}
            </Badge>
            <Button
              size="sm"
              variant="secondary"
              className="mt-3 block"
              loading={test.isPending}
              onClick={() => test.mutate()}
            >
              Test connection
            </Button>
          </div>
        </div>

        {result && (
          <div className="mt-4">
            <Alert tone={result.ok ? "good" : "bad"}>
              <p className="font-medium">{result.detail}</p>
              {result.ok && result.services && (
                <p className="mt-1 text-sm">
                  {result.rate_count} service{result.rate_count === 1 ? "" : "s"} returned in{" "}
                  {result.elapsed_ms} ms: {result.services.join(", ")}
                </p>
              )}
            </Alert>
          </div>
        )}
      </Card>

      <Card className="mb-6">
        <h2 className="text-lg font-bold tracking-tight">Update credentials</h2>
        <p className="mb-4 mt-1 text-sm text-ink-500">
          Leave a field blank to keep what is stored. Clearing a saved value puts that setting back
          to this server's environment default.
        </p>

        {error && (
          <div className="mb-4">
            <Alert>{error}</Alert>
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          {data.editable.map((setting) => {
            const current = stored(setting.key);
            return (
              <Field
                key={setting.key}
                label={setting.label}
                type={setting.type === "secret" ? "password" : setting.type === "number" ? "number" : "text"}
                autoComplete="off"
                value={draft[setting.key] ?? ""}
                onChange={(event) => setDraft({ ...draft, [setting.key]: event.target.value })}
                error={fields[setting.key]}
                placeholder={
                  setting.type === "secret"
                    ? current?.is_set
                      ? `Stored: ${current.masked}`
                      : "Paste the key from ShipLogic"
                    : undefined
                }
                hint={setting.help || undefined}
                className={setting.type === "secret" ? "sm:col-span-2" : undefined}
              />
            );
          })}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save and apply
          </Button>
          <Button variant="secondary" onClick={() => setDraft({})}>
            Reset form
          </Button>
        </div>
        <p className="mt-3 text-xs text-ink-400">
          The key is encrypted before it is stored and is never shown again, here or anywhere else.
        </p>
      </Card>

      <RateTester />

      <h2 className="mb-3 text-lg font-bold tracking-tight">Change history</h2>
      {data.changes.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-500">Nothing has been changed yet.</p>
        </Card>
      ) : (
        <Table head={["When", "Setting", "Change", "Value", "Who"]}>
          {data.changes.map((change) => (
            <tr key={change.id}>
              <td className="ws-td whitespace-nowrap text-ink-500">{dateTime(change.created_at)}</td>
              <td className="ws-td font-mono text-xs">{change.key}</td>
              <td className="ws-td">
                <Badge tone={change.action === "cleared" ? "attention" : "neutral"}>{change.action}</Badge>
              </td>
              <td className="ws-td font-mono text-xs text-ink-500">{change.masked_hint || "-"}</td>
              <td className="ws-td text-ink-600">{change.actor_email}</td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-6 sm:block">
      <dt className="text-ink-500">{label}</dt>
      <dd className="font-medium sm:mt-0.5">{value}</dd>
    </div>
  );
}

